import React from 'react';
import { TrendingDown, TrendingUp, CornerDownRight, Unlink2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import type { Category, Transaction } from '@/lib/api';
import type { RowDecision, RowPair } from './useOFXReconciliation';

interface LinkedEntryRowProps {
  pair: RowPair;
  decision: RowDecision;
  categoriesById: Record<string, Category>;
  onUnlink?: (fitid: string) => void;
  errorMessage?: string;
}

function compactDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

function findMatched(pair: RowPair, manualId: string | null): Transaction | null {
  if (!manualId) return null;
  return pair.row.matching_transactions?.find((m) => m.id === manualId) ?? null;
}

export const LinkedEntryRow: React.FC<LinkedEntryRowProps> = ({
  pair,
  decision,
  categoriesById,
  onUnlink,
  errorMessage,
}) => {
  const { row, fitid } = pair;
  const isIncome = row.type === 'income';
  const matched = findMatched(pair, decision.manualId);
  const matchedCategoryName = matched
    ? categoriesById[matched.category_id]?.name ?? 'Categoria desconhecida'
    : '';

  return (
    <div className="px-4 py-3 animate-fade-in group">
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0 w-10">
          {compactDate(row.date)}
        </span>
        <span
          className={cn(
            'inline-flex items-center justify-center h-5 w-5 rounded-full shrink-0',
            isIncome ? 'bg-income/12 text-income' : 'bg-expense/12 text-expense',
          )}
          aria-label={isIncome ? 'Receita' : 'Despesa'}
        >
          {isIncome ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        </span>
        <span className="text-sm text-foreground truncate min-w-0 flex-1" title={row.description}>
          {row.description}
        </span>
        <span
          className={cn(
            'text-sm font-medium tabular-nums whitespace-nowrap shrink-0',
            isIncome ? 'text-income' : 'text-expense',
          )}
        >
          {formatCurrency(row.amount)}
        </span>
      </div>

      {matched && (
        <div className="mt-1.5 pl-[3.25rem] flex items-start gap-2">
          <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-foreground/80 truncate" title={matched.description}>
              {matched.description}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {matchedCategoryName} · {compactDate(matched.date)}
            </p>
          </div>
          {onUnlink && (
            <button
              type="button"
              onClick={() => onUnlink(fitid)}
              className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 px-2 py-1 rounded-md transition-colors flex items-center gap-1 shrink-0 focus-visible:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
              title="Desvincular e criar uma nova entrada"
            >
              <Unlink2 className="h-3 w-3" />
              <span className="hidden sm:inline">Desvincular</span>
            </button>
          )}
        </div>
      )}

      {errorMessage && (
        <p className="mt-2 pl-[3.25rem] text-xs text-destructive">{errorMessage}</p>
      )}
    </div>
  );
};
